import {
  useViewContactsSuspense,
  type ViewContactsRequest,
  type ViewContactsResponse
} from "@swivu/client-api";
import { createContext, Suspense, useContext } from "react";
import { Link } from "react-router-dom";
import { cn, decodeFA } from "../utils/index.js";
import { Button, buttonActions } from "./Button.js";
import ErrorBoundary from "./ErrorBoundary.js";
import SuspenseWithDelay from "./SuspenseWithDelay.js";
import { useIsGummy } from "../hooks/useGummyTheme.js";
import { tilesForIds } from "../utils/tilePalette.js";

type ViewContactsVariables = ViewContactsRequest & {
  baseUrl: string;
};
type ViewContactsProps = ViewContactsVariables & {
  contacts: ViewContactsResponse;
};

const ViewContactsContext = createContext<ViewContactsProps | null>(null);

const ViewContactsProviderWrapper = ({
  children,
  ...props
}: ViewContactsVariables & { children: React.ReactNode }) => {
  return (
    <SuspenseWithDelay fallback={<div>Loading</div>}>
      <ErrorBoundary>
        <ViewContactsProvider {...props}>{children}</ViewContactsProvider>
      </ErrorBoundary>
    </SuspenseWithDelay>
  );
};

const ViewContactsProvider = ({
  children,
  ...props
}: ViewContactsVariables & {
  children: React.ReactNode;
}) => {
  const { data: contacts } = useViewContactsSuspense({
    variables: {
      ...props
    }
  });
  return (
    <ViewContactsContext.Provider value={{ ...props, contacts }}>
      {children}
    </ViewContactsContext.Provider>
  );
};

const useViewContacts = () => {
  const context = useContext(ViewContactsContext);
  if (!context) {
    throw new Error(
      "useViewContacts must be used within a ViewContactsProvider"
    );
  }
  return context;
};

const ViewContacts = () => {
  const { contacts, baseUrl } = useViewContacts();
  const isGummy = useIsGummy();
  const tileColors = tilesForIds(contacts.map((c) => c.id));

  const subItems = [
    { field: "address", icon: "location-dot" },
    { field: "phone", icon: "phone" },
    { field: "email", icon: "envelope" },
    { field: "category", icon: "tag" }
  ] as const;

  const gummyCircleButton = cn(
    "inline-flex h-12 w-12 items-center justify-center rounded-full",
    "shadow-pill bg-white/95 text-mono-900",
    "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96] motion-reduce:transform-none",
    "outline-none focus-visible:ring-4 focus-visible:ring-white/80"
  );

  return (
    <div>
      <div className="mx-auto pb-4">
        <ul
          className={cn(
            "grid grid-cols-1 sm:grid-cols-1",
            isGummy
              ? "gap-3"
              : "divide-mono-300 dark:divide-mono-700/50 divide-y"
          )}
        >
          {contacts.map((contact, i) => {
            const contacts = new URLSearchParams({
              ids: `${contact.id}`,
              name: `${contact.name}`
            });
            return (
              <li
                key={contact.id}
                className={cn(
                  "relative flex",
                  isGummy
                    ? cn(
                        "relative overflow-hidden px-5",
                        "rounded-surface-lg",
                        tileColors[i],
                        "shadow-card"
                      )
                    : "border-mono-300 dark:border-mono-700/50 hover:border-mono-400 text-mono-700 dark:text-mono-300"
                )}
              >
                {isGummy && <span aria-hidden className="top-gloss" />}

                <div
                  className={cn(
                    "flex min-w-0 flex-1 flex-col gap-2 sm:flex-row md:gap-4",
                    isGummy ? "py-5" : "py-5"
                  )}
                >
                  <div className="flex-1 focus:outline-none">
                    <div>
                      <p
                        style={{
                          viewTransitionName: `contact-name-${contact.id}`
                        }}
                        className={cn(
                          "mobile-pref-card-title",
                          isGummy
                            ? "font-display text-xl font-black tracking-tight text-shadow-on-color"
                            : "text-base font-medium"
                        )}
                      >
                        {contact.name}
                      </p>
                      {subItems.reduce((acc, item) => {
                        if (contact[item.field]) {
                          acc.push(
                            <p
                              key={item.field}
                              className={cn(
                                "mobile-pref-card-meta flex items-center gap-3 pt-2",
                                isGummy
                                  ? "text-sm font-bold text-white text-shadow-on-color opacity-95"
                                  : "text-xs leading-4 opacity-60 items-start"
                              )}
                            >
                              <i
                                className={cn(
                                  `fa-regular ${decodeFA(item.icon)}`,
                                  isGummy
                                    ? "text-xl text-shadow-on-color"
                                    : "mobile-pref-view-inline-icon"
                                )}
                              />
                              <span>{contact[item.field]}</span>
                            </p>
                          );
                        }
                        return acc;
                      }, [] as React.JSX.Element[])}
                      {contact.linked_to.length > 0 && (
                        <div
                          className={cn(
                            "mobile-pref-card-meta flex items-center gap-3 pt-1",
                            isGummy
                              ? "text-sm font-bold text-white text-shadow-on-color opacity-95"
                              : "text-xs leading-4 opacity-60 items-start"
                          )}
                        >
                          <i
                            className={cn(
                              `fa-regular ${decodeFA(`link-horizontal`)}`,
                              isGummy
                                ? "text-xl text-shadow-on-color"
                                : "mobile-pref-view-inline-icon"
                            )}
                          />
                          <span>
                            {contact.linked_to.map((item) => item).join(", ")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`flex flex-col justify-between gap-6`}>
                    {contact.status && (
                      <div className="absolute -right-0 top-5 md:static  md:flex justify-end">
                        <span
                          className={cn(
                            "mobile-pref-card-meta flex items-center justify-center rounded-full",
                            isGummy
                              ? "shadow-pill bg-white/95 text-mono-900 px-3 py-1 text-sm font-black uppercase tracking-wide"
                              : "dark:bg-mono-950 border-mono-300 dark:border-mono-700/50 border bg-white px-2 py-1 text-xs font-bold"
                          )}
                        >
                          {contact.status}
                        </span>
                      </div>
                    )}

                    <div className="dark:border-mono-700/50 mt-1 flex flex-row-reverse flex-wrap items-start justify-end gap-3 md:mt-0 md:flex-row">
                      {contact.phone &&
                        (isGummy ? (
                          <button
                            type="button"
                            className={gummyCircleButton}
                            aria-label={`Call ${contact.name}`}
                            onClick={() => {
                              window.location.href = `tel:${contact.phone}`;
                            }}
                          >
                            <i
                              className={`fa-regular text-2xl ${decodeFA(`phone`)}`}
                            />
                          </button>
                        ) : (
                          <Button
                            className="mobile-pref-control bg-white dark:bg-mono-950 border-mono-300 dark:border-mono-700/50 text-mono-500 border md:px-4 md:py-1"
                            action={`secondary`}
                            aria-label={`Call ${contact.name}`}
                            onClick={() => {
                              window.location.href = `tel:${contact.phone}`;
                            }}
                          >
                            <i
                              className={`mobile-pref-view-icon fa-regular ${decodeFA(`phone`)}`}
                            />
                          </Button>
                        ))}
                      {contact.email &&
                        (isGummy ? (
                          <button
                            type="button"
                            className={gummyCircleButton}
                            aria-label={`Email ${contact.name}`}
                            onClick={() => {
                              window.location.href = `mailto:${contact.email}`;
                            }}
                          >
                            <i
                              className={`fa-regular text-2xl ${decodeFA(`envelope`)}`}
                            />
                          </button>
                        ) : (
                          <Button
                            className="mobile-pref-control bg-white dark:bg-mono-950 border-mono-300 dark:border-mono-700/50 text-mono-500 border md:px-4 md:py-1"
                            action={`secondary`}
                            aria-label={`Email ${contact.name}`}
                            onClick={() => {
                              window.location.href = `mailto:${contact.email}`;
                            }}
                          >
                            <i
                              className={`mobile-pref-view-icon fa-regular ${decodeFA(`envelope`)}`}
                            />
                          </Button>
                        ))}
                      <Link
                        viewTransition={true}
                        to={`${baseUrl}/${contact.id}?${contacts.toString()}`}
                        className={cn(
                          "relative transition-all active:scale-95",
                          isGummy
                            ? gummyCircleButton
                            : cn(
                                "mobile-pref-control",
                                `rounded-md px-6 py-2 text-lg ${buttonActions.secondary.default} dark:bg-mono-950 text-mono-500`,
                                "bg-white border-mono-300 dark:border-mono-700/50 border",
                                "md:px-4 md:py-1"
                              )
                        )}
                        aria-label={`View ${contact.name}`}
                      >
                        <i
                          className={cn(
                            `fa-regular ${decodeFA(`circle-info`)}`,
                            isGummy ? "text-2xl" : "mobile-pref-view-icon"
                          )}
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const ViewContactsWrapper = () => {
  // should not happen with routing

  return (
    <Suspense fallback={<ViewContactsSkeleton />}>
      <ErrorBoundary>
        <ViewContacts />
      </ErrorBoundary>
    </Suspense>
  );
};

const ViewContactsSkeleton = () => {
  return (
    <div className="flex w-full animate-pulse flex-col gap-2">
      <div className="dark:bg-mono-900  h-10  rounded bg-gray-200" />
      <div className="dark:bg-mono-900  h-10  rounded bg-gray-200" />
      <div className="dark:bg-mono-900  h-16    rounded bg-gray-200" />
    </div>
  );
};

ViewContactsWrapper.Skeleton = ViewContactsSkeleton;
ViewContactsWrapper.Provider = ViewContactsProviderWrapper;
ViewContactsWrapper.useViewContacts = useViewContacts;
export default ViewContactsWrapper;
